/**
 * Simple Cart Service - demonstrates "dumb frontend" approach
 * Frontend only sends IDs and quantities, backend calculates ALL prices
 */

import httpClient from './base-service'

export interface SimpleCartItem {
  menuItemId: number
  quantity: number
}

export interface SimpleCartRequest {
  items: SimpleCartItem[]
  tipPercentage?: number
}

export interface SimpleCartItemResponse {
  menuItemId: number
  itemName: string
  quantity: number
  unitPrice: number
  totalPrice: number
  taxAmount: number
  taxPercentage: number
}

export interface SimpleCartResponse {
  items: SimpleCartItemResponse[]
  subtotal: number
  taxAmount: number
  tipAmount: number
  total: number
}

export class SimpleCartService {
  /**
   * Calculate cart totals - frontend sends only IDs, backend does all math
   */
  static async calculateCart(
    items: SimpleCartItem[],
    tipPercentage = 0
  ): Promise<SimpleCartResponse> {
    const request: SimpleCartRequest = {
      items,
      tipPercentage,
    }

    try {
      const response = await httpClient.post('/SimpleCart/calculate', request)
      return response.data?.data as SimpleCartResponse
    } catch (error) {
      throw new Error(
        `Failed to calculate cart: ${
          (error as Error).message || 'Unknown error'
        }`
      )
    }
  }

  /**
   * Add item to cart and recalculate
   */
  static async addItemToCart(
    currentItems: SimpleCartItem[],
    newMenuItemId: number,
    quantity = 1,
    tipPercentage = 0
  ): Promise<SimpleCartResponse> {
    // Find existing item or add new one
    const existingItemIndex = currentItems.findIndex(
      (item) => item.menuItemId === newMenuItemId
    )

    let updatedItems: SimpleCartItem[]
    if (existingItemIndex >= 0) {
      // Update existing item quantity
      updatedItems = [...currentItems]
      updatedItems[existingItemIndex].quantity += quantity
    } else {
      // Add new item
      updatedItems = [...currentItems, { menuItemId: newMenuItemId, quantity }]
    }

    // Backend calculates everything
    return SimpleCartService.calculateCart(updatedItems, tipPercentage)
  }

  /**
   * Update item quantity and recalculate
   */
  static async updateItemQuantity(
    currentItems: SimpleCartItem[],
    menuItemId: number,
    newQuantity: number,
    tipPercentage = 0
  ): Promise<SimpleCartResponse> {
    let updatedItems: SimpleCartItem[]

    if (newQuantity <= 0) {
      // Remove item
      updatedItems = currentItems.filter(
        (item) => item.menuItemId !== menuItemId
      )
    } else {
      // Update quantity
      updatedItems = currentItems.map((item) =>
        item.menuItemId === menuItemId
          ? { ...item, quantity: newQuantity }
          : item
      )
    }

    // Backend calculates everything
    return SimpleCartService.calculateCart(updatedItems, tipPercentage)
  }

  /**
   * Update tip percentage and recalculate
   */
  static async updateTip(
    currentItems: SimpleCartItem[],
    tipPercentage: number
  ): Promise<SimpleCartResponse> {
    // Backend recalculates with new tip
    return SimpleCartService.calculateCart(currentItems, tipPercentage)
  }
}
